import type { ReactNode } from 'react';
import type { ServiceType } from '@common/enums/index';
import { getServiceLabel } from '@common/customControls/getServiceLabel';
import { getDisplayName } from '@common/utils/getDisplayName';
import { useFrontendSettings } from '@frontend/utils/hooks/useFrontendSettings';
import { useEpisodeContext } from '@frontend/utils/index';
import type { IProcedureGetOne } from '../../../../interfaces/procedureGetOne';
import type { IProcess } from '../../../../interfaces/process';
import type { IServiceGetOne } from '../../../../interfaces/serviceGetOne';
import type { IItemState } from '../../../episodes/Episode';
import { EditProcessContainer, ProcessContainer } from '../../../episodes/common';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';

interface INewOrEditProcedureProcessContainerProps {
    children: ReactNode;
    currentProcedure: IProcedureGetOne | undefined;
    isBioGrader: boolean | undefined;
    isFirstItem: boolean;
    itemState?: IItemState;
    process: IProcess | undefined;
    service: IServiceGetOne;
    serviceType?: ServiceType;
}

export function NewOrEditProcedureProcessContainer(props: INewOrEditProcedureProcessContainerProps) {
    const { patient } = useEpisodeContext();
    const { frontendSettings } = useFrontendSettings();
    const commonProcessItemHeaderProps = {
        isItemOpen: true,
        isFirstItem: props.isFirstItem,
        process: props.process,
        showProcessIcon: true,
        serviceType: props.serviceType || props.service.serviceType,
        title: getServiceLabel(props.service.code, 'procedure', props.service.name),
    };

    if (props.isBioGrader && (!props.currentProcedure || !props.currentProcedure.id)) {
        return (
            <EditProcessContainer
                experimentalCarepathOphthalmologyEnabled={frontendSettings.experimentalCarepathOphthalmologyEnabled}
                isActiveActivity={props.itemState?.initialIsOpen}
            >
                <ProcessItemHeader {...commonProcessItemHeaderProps} />
                {props.children}
            </EditProcessContainer>
        );
    } else {
        return (
            <ProcessContainer isActiveActivity={props.itemState?.initialIsOpen}>
                <ProcessItemHeader
                    {...commonProcessItemHeaderProps}
                    organizationName={
                        patient.authorization?.canSeePatientDetails &&
                        props.currentProcedure?.performerOrganization.name
                            ? props.currentProcedure.performerOrganization.name
                            : ''
                    }
                    practitionerName={
                        patient.authorization &&
                        patient.authorization.canSeePatientDetails &&
                        props.currentProcedure &&
                        props.currentProcedure.performerPractitioner
                            ? getDisplayName(props.currentProcedure.performerPractitioner)
                            : undefined
                    }
                />
                {props.children}
            </ProcessContainer>
        );
    }
}
